import { type AxiosPromise } from 'axios';
import format from 'date-fns/format';
import * as moment from 'moment';

import type APIAddress from '~/types/APIAddress';
import type Address from '~/types/Address';
import type LabelFormat from '~/types/LabelFormat';
import type Parcel from '~/types/Parcel';

import { SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import { DATE_FORMAT_WITH_TIMEZONE } from '~/constants/date';
import { type PrintPlan } from '~/modules/print/types/printPlan.type';
import api, { apiV1, armorAPI, webOrdersApi } from '~/services/api';
import {
  type AddressReturnType,
  type AddressToType,
  type AddressType,
  type BaseAddress,
} from '~/types/Address';
import { toJSON } from '~/types/Address';
import { type JSONOrder, type newOrder } from '~/types/Order';
import {
  extrasToJSON,
  type JSONBatchEntriesResponse,
  type JSONBatchEntryExtended,
  type JSONPartialFulfillmentContents,
  type JSONShipmentExtras,
  type ShipmentExtras,
} from '~/types/OrderBatchEntry';
import { type BatchEntryId, type JSONOrdersBatch } from '~/types/OrdersBatch';
import { type ParcelRequest, type SavedJSONParcel } from '~/types/Parcel';
import { toJSON as parcelToJSON } from '~/types/Parcel';
import { type JSONTransactionUI2 } from '~/types/Transaction';
import { observability } from '~/utils/observability';
import { clearPendingRequests, hasPendingRequests } from '~/utils/request-watcher';

const X_ORDERS_SEARCH_VERSION = 'ORDERS_SEARCH_ORDER_NUMBER';

export const getAll = (
  page: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  fetchCount: string,
  showOnlyHidden: boolean,
  sort: string,
  sortBy: string,
  orderBy: string,
) =>
  armorAPI.request<{
    count: number;
    results: JSONOrder[];
  }>({
    headers: { X_ORDERS_SEARCH_VERSION },
    method: 'GET',
    params: {
      ...filters,
      page,
      sort: sort !== '' ? sort : undefined,
      ...(sortBy?.length ? { sort_by: sortBy } : {}),
      ...(orderBy?.length ? { order_by: orderBy } : {}),
      ...(fetchCount?.length ? { results: fetchCount } : {}),
      hidden: showOnlyHidden,
      ...(filters.is_international
        ? { destination: filters.is_international === 'true' ? 'international' : 'domestic' }
        : {}),
      is_return_included: true,
    },
    url: '/orders',
  });

export const getWithReturns = (orderId: string) =>
  armorAPI.request<JSONOrder>({
    method: 'GET',
    params: {
      is_return_included: true,
    },
    url: `/orders/${orderId}`,
  });

export const getOrderTransactions = (orderObjectId: string) =>
  apiV1.request<{ results: JSONTransactionUI2[] }>({
    method: 'GET',
    params: {
      results: 200,
    },
    url: `/orders/${orderObjectId}/transactions`,
  });

export const createOrder = (order: ReturnType<typeof newOrder>) =>
  apiV1.request<JSONOrder>({
    data: order,
    method: 'POST',
    url: 'orders/',
  });

export const countOrders = () =>
  armorAPI.request<{ results: JSONOrder[] }>({
    headers: { X_ORDERS_SEARCH_VERSION },
    method: 'GET',
    params: {
      page: 1,
      results: 1,
    },
    url: '/orders',
  });

export const getBatch = (batchId: number) =>
  armorAPI.request<JSONOrdersBatch>({
    method: 'GET',
    url: `/batch/${batchId}`,
  });

export const createBatch = (orderObjectIds: string[], batchEntryIds: number[]) =>
  armorAPI.request<JSONOrdersBatch>({
    data: {
      batch_entries: batchEntryIds,
      orders: orderObjectIds,
    },
    method: 'POST',
    url: `/batch/`,
  });

export const createBatchTransactions = (
  batchId: number,
  rates: string[],
  excludedOrders: string[],
) =>
  armorAPI.request<{
    batch_entries: { id: number }[];
    label_limit_renewal_date: string;
    remaining_labels_until_limit: number;
    // TODO: Verify this type!
    transactions: { batch_entry_id: number; object_id: string }[];
  }>({
    data: { excluded_orders: excludedOrders, rates },
    method: 'POST',
    url: `/batch/${batchId}/transactions`,
  });

export const syncOrders = () =>
  api.request({
    method: 'GET',
    url: 'orders/sync',
  });

export const getOrderPackingSlip = (orderObjectIds: string[], batchEntryIds: number[]) =>
  api.request<{ slip_url: string }>({
    method: 'GET',
    params: {
      id: orderObjectIds,
      only_be_id: batchEntryIds,
    },
    url: `orders/packingslip`,
  });

export const getAllLabels = (orderObjectIds: string[], batchEntryIds: BatchEntryId[]) =>
  apiV1.request<{ url: string }>({
    data: {
      action: {
        list: orderObjectIds,
        type: 'merge_labels',
      },
      batch_entries: batchEntryIds,
    },
    method: 'POST',
    url: 'orders/',
  });

export const getLabel = (transactionIds: string[], fileFormat: string) =>
  armorAPI.request({
    data: {
      legacy_file_format: fileFormat,
      transactions: transactionIds,
    },
    method: 'POST',
    url: 'label_regen/',
  });

export const printLabels = (transactionObjectIds: string[], labelFiletype: LabelFormat) =>
  apiV1.request<PrintPlan>({
    data: {
      label_filetype: labelFiletype,
      transactions: transactionObjectIds,
    },
    method: 'POST',
    url: 'transactions/print_labels',
  });

export const hideOrder = (orderObjectIds: string[]) =>
  apiV1.request({
    data: {
      action: {
        list: orderObjectIds,
        type: 'hide',
      },
    },
    method: 'POST',
    url: 'orders/',
  });

export const createBatchEntriesForOrders = (orderObjectIds: string[], isRating?: boolean) =>
  armorAPI.request<JSONBatchEntryExtended[]>({
    data: {
      is_rating: !!isRating,
      orders: orderObjectIds,
    },
    method: 'POST',
    url: 'orders/batch_entry',
  });

export const getOrCloneBatchEntriesForOrders = (
  orderObjectIDs: string[],
  batchEntryIDs: number[],
) =>
  armorAPI.request<JSONBatchEntryExtended[]>({
    data: {
      batch_entries: batchEntryIDs,
      is_integrated: true,
      orders: orderObjectIDs,
    },
    method: 'POST',
    url: 'orders/batch_entry',
  });

const buildRequestParcel = (parcel: SavedJSONParcel) => {
  const parcelData = parcel.use_imported_order_weight
    ? {
        use_imported_order_weight: parcel.use_imported_order_weight,
      }
    : {
        mass_unit: parcel.mass_unit,
        weight: parcel.weight,
      };

  const carrierTemplateVariableDimensions =
    parcel.carrier_parcel_template && parcel.carrier_parcel_template.is_variable_dimensions;

  const requestParcel = parcel.user_parcel_template
    ? {
        ...parcelData,
        user_parcel_template_object_id:
          parcel.user_parcel_template && parcel.user_parcel_template.object_id,
      } // user parcel template
    : parcel.carrier_parcel_template && !carrierTemplateVariableDimensions
    ? {
        ...parcelData,
        carrier_parcel_template_token:
          parcel.carrier_parcel_template && parcel.carrier_parcel_template.token,
      } // carrier parcel template, not variable dimension
    : parcel.carrier_parcel_template && carrierTemplateVariableDimensions
    ? {
        ...parcelData,
        carrier_parcel_template_token:
          parcel.carrier_parcel_template && parcel.carrier_parcel_template.token,
        distance_unit: parcel.distance_unit,
        height: parcel.height,
        length: parcel.length,
        width: parcel.width,
      } // carrier parcel template, variable dimension (needs dimensions in payload)
    : {
        ...parcelData,
        distance_unit: parcel.distance_unit,
        height: parcel.height,
        length: parcel.length,
        width: parcel.width,
      }; // custom dimensions

  return requestParcel;
};

export const patchBatchEntries = (
  batchEntryIds: number[],
  parcel: Maybe<Parcel>,
  extras: Maybe<Partial<ShipmentExtras>>,
): AxiosPromise<JSONBatchEntryExtended[]> => {
  const requestParcel = parcel ? buildRequestParcel(parcelToJSON(parcel)) : undefined;
  const requestExtras = extras ? extrasToJSON(extras) : undefined;

  return armorAPI.request({
    data: {
      batch_entries: batchEntryIds,
      extras: requestExtras,
      parcel: requestParcel,
    },
    method: 'PATCH',
    url: 'batch_entry/',
  });
};

export const patchParcel = (
  orderShipmentIds: number[],
  parcel: ParcelRequest,
): AxiosPromise<JSONBatchEntryExtended[]> =>
  armorAPI.request({
    data: {
      batch_entries: orderShipmentIds,
      parcel,
    },
    method: 'PATCH',
    url: 'batch_entry/',
  });

export const patchExtras = (
  orderShipmentIds: number[],
  extras: Partial<JSONShipmentExtras>,
): AxiosPromise<JSONBatchEntryExtended[]> =>
  armorAPI.request({
    data: {
      batch_entries: orderShipmentIds,
      extras,
    },
    method: 'PATCH',
    url: 'batch_entry/',
  });

export const updateParcel = (batchEntryIds: number[], parcel: SavedJSONParcel) => {
  const requestParcel = buildRequestParcel(parcel);

  return armorAPI.request<JSONBatchEntryExtended[]>({
    data: {
      batch_entries: batchEntryIds,
      parcel: requestParcel,
    },
    method: 'PUT',
    url: 'orders/batch_entry/parcel',
  });
};

export const updateBatchEntryExtras = (
  batchEntryIds: number[],
  extras: ShipmentExtras,
): AxiosPromise<JSONBatchEntryExtended[]> =>
  armorAPI.request({
    data: {
      batch_entries: batchEntryIds,
      extras: extrasToJSON(extras),
    },
    method: 'PUT',
    url: 'orders/batch_entry/extras',
  });

export const patchUpdateBatchEntryExtras = (
  batchEntryIds: number[],
  extras: Partial<ShipmentExtras>,
) =>
  armorAPI.request<JSONBatchEntryExtended[]>({
    data: {
      batch_entries: batchEntryIds,
      extras: extrasToJSON(extras),
    },
    method: 'PATCH',
    url: 'orders/batch_entry/extras',
  });

export const updateBatchEntryAddress = (
  address: BaseAddress | number,
  addressType: AddressType,
  batchEntryIds: number[],
) => {
  const parsedAddress = (address as APIAddress).id
    ? (address as APIAddress).id
    : toJSON(address as Address);
  const convertedAddressType: 'from' | AddressReturnType | AddressToType =
    addressType === SENDER_ADDRESS_KIND ? 'from' : addressType; // Conversion needed since BE uses 'from' but FE uses 'sender'
  const url = `orders/batch_entry/address_${convertedAddressType}`;
  const addressKey =
    typeof parsedAddress === 'number'
      ? `address_${convertedAddressType}_id`
      : `address_${convertedAddressType}`;

  const data = {
    [addressKey]: parsedAddress,
    batch_entries: batchEntryIds,
  };

  return armorAPI.request<JSONBatchEntryExtended[]>({
    data,
    method: 'PUT',
    url,
  });
};

export const updateBatchEntryServiceLevelToken = (
  preferredServiceLevelToken: string,
  batchEntryIds: number[],
  accountObjectId?: string,
) =>
  armorAPI.request<JSONBatchEntriesResponse>({
    data: {
      batch_entries: batchEntryIds,
      preferred_account_object_id: accountObjectId,
      preferred_service_level_token: preferredServiceLevelToken,
    },
    method: 'PUT',
    url: `batch_entry/service_level`,
  });

export const updateBatchEntryShipmentDate = (
  shipmentDate: moment.Moment | number,
  batchEntryIds: number[],
) =>
  armorAPI.request<JSONBatchEntryExtended[]>({
    data: {
      batch_entries: batchEntryIds,
      shipment_date: moment.isMoment(shipmentDate)
        ? shipmentDate.format() // preserves original timezone instead of converting to UTC
        : format(shipmentDate, DATE_FORMAT_WITH_TIMEZONE),
    },
    method: 'PUT',
    url: '/batch_entry/shipment_date',
  });

const refreshBatchEntryRequest = () => {
  let controller;
  return (batchEntryIds: number[]) => {
    const refreshURL = '/orders/batch_entry/refresh';
    if (controller && hasPendingRequests(refreshURL)) {
      observability.singleEvent('RETRY_SHIPMENT_CANCELLED');
      controller.abort();
      clearPendingRequests(refreshURL);
    }
    controller = new AbortController();

    return armorAPI.request<JSONBatchEntryExtended[]>({
      data: {
        batch_entries: batchEntryIds,
      },
      method: 'PUT',
      signal: controller.signal,
      url: '/orders/batch_entry/refresh',
    });
  };
};

const refreshBatchEntryWithCancel = refreshBatchEntryRequest();

export const refreshBatchEntry = (batchEntryIds: number[]) =>
  refreshBatchEntryWithCancel(batchEntryIds);

export const getOrderBatchEntry = (orderObjectId: number | string, includeReturns = false) =>
  armorAPI.request<JSONBatchEntryExtended[]>({
    method: 'GET',
    params: {
      is_return_included: includeReturns,
    },
    url: `/orders/${orderObjectId}/batch_entry`,
  });

export const getOrderBatchEntries = ({
  includeReturns = false,
  orderObjectIds,
  transactionObjectIds,
}: {
  includeReturns?: boolean;
  orderObjectIds?: string[];
  transactionObjectIds?: string[];
}) =>
  armorAPI.request<{ results: JSONBatchEntryExtended[] }>({
    method: 'GET',
    params: {
      is_return_included: includeReturns,
      orders: orderObjectIds,
      transactions: transactionObjectIds,
    },
    url: '/batch_entry',
  });

export const fetchBatchEntryRules = (batchEntryId: number) =>
  armorAPI.request({
    method: 'GET',
    url: `/batch_entry/${batchEntryId}/rule`,
  });

// Partial fulfillment / order split services
export const createPartialFulfillmentRequests = (
  orderObjectId: number | string,
  partialFulfillmentRequests: JSONPartialFulfillmentContents[],
) =>
  armorAPI.request<JSONOrder[]>({
    data: {
      shipments: partialFulfillmentRequests,
    },
    method: 'POST',
    url: `orders/${orderObjectId}/split`,
  });

/**
 * Example of using the orders facade generated client, to be used in the future :)
 */
export const getOrders = webOrdersApi.getWebOrders;
